import { ImageName } from "@mcleod/images";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { BlurEvent, ChangeEvent, ClickEvent, CloneComponent, Component, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, Image, Label, Layout, LookupModelSelectionEvent, MapVendor, Panel, SaveButton, SelectionEvent, SlideoutDecorator, Snackbar, Tab, Textbox, Toast, ValidationResult, Switch, Checkbox, EditRowDecorator } from "@mcleod/components";
import { Alignment, Api, DatePart, DateUtil, ModelRow, StringUtil, Timezone } from "@mcleod/core";
import { ErrorParam } from "@mcleod/core/src/ErrorHandler";
import { LocationContactQuickInfo } from "../LocationContactQuickInfo";
import { ModelServicefail } from "../models/ModelServicefail";
import { MovementTabs } from "../MovementTabs";
import { OrderStop } from "../OrderStop";
import { PlanningUtil } from "../planning/PlanningUtil";
import { AutogenLayoutDispatch } from "./autogen/AutogenLayoutDispatch";
import { EquipmentGroupValidation, EquipmentGroupValidationResult } from "./EquipmentGroupValidation";
import { Action } from "./Seating";
import { SelectTrailerDialog } from "./SelectTrailerDialog";
import { MultiStop } from "../MultiStop";
import { getDispatchControlBoolean, getDispatchControlValue } from "@mcleod/general";
import { OrderTabView } from "../OrderTabView";
import { DetentionUtil } from "../DetentionUtil";
import { setCommodityTooltipContent } from "@mcleod/powerbroker";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";

interface DispatchSlideoutProps {
    equipmentId: string;
    equipmentType: string;
    orderId?: string;
    manifestId?: string;
    slideoutTitle?: string;
    doAfterSlideout?: (hasChanged: boolean) => void;
}

const saveBtnProps = { rowBreak: false, marginRight: 8 }

export class Dispatch extends AutogenLayoutDispatch {

    public equipmentId: string;
    public equipmentType: string;
    public orderId: string;
    public manifestId: string;
    public dispatchButton: SaveButton;
    private tieEmptyMovementId: string;
    private emptyPosition: string;
    private hasChanged: boolean = false;
    private _ordersCustomerId;
    private stopForLocate = null;
    private nextStop: ModelRow;
    private validationResult: EquipmentGroupValidationResult;
    private isInitializingDropTrailer: boolean = false;
    private maxTrailers: number;
    private static readonly AUDIT_LOG_TEXT = "Audit log";
    private static readonly ORDER_FIELD = "order_id";

    static openSlideout(props: DispatchSlideoutProps) {
        const dispatchLayout = Layout.getLayout("lme/dispatch/asset/Dispatch") as Dispatch;
        const { equipmentId, equipmentType, orderId, manifestId, slideoutTitle } = props;
        new SlideoutDecorator({
            layout: dispatchLayout,
            fillVerticalSpace: true,
            width: "85%",
            title: slideoutTitle,
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            addlComponents: dispatchLayout.createHeaderButtons(),
            doAfterSlideIn: () => {
                dispatchLayout.equipmentId = equipmentId;
                dispatchLayout.equipmentType = equipmentType;
                dispatchLayout.orderId = orderId;
                dispatchLayout.addTabs();
                dispatchLayout.dispatchButton.doOnDataSourceChanged = (ds, value) => {
                    let enabled: boolean;
                    if (dispatchLayout.switchDispatchAction.checked) {
                        enabled = !EquipmentGroupValidation.hasDriverRegulatoryErrors(dispatchLayout.validationResult) && value;
                    } else {
                        enabled = !EquipmentGroupValidation.hasDispatchErrors(dispatchLayout.validationResult) && value;
                    }
                    dispatchLayout.dispatchButton.enabled = enabled;
                }
                dispatchLayout.addDispatchToChangedComp();
            },
            doAfterSlideOut: () => props.doAfterSlideout?.(dispatchLayout.hasChanged),
        })
    }

    createHeaderButtons(): Component[] {
        this.dispatchButton = SaveButton.create({
            caption: "Save", ...saveBtnProps,
            onClick: () => this.performSave(),
            disabledTooltip: "You cannot save this record because nothing has changed."
        });
        return [this.dispatchButton];
    }

    onLoad(): void {
        this.maxTrailers = getDispatchControlValue("maximum_trailers");
        this.tabCurrent.select();
        this.tabsetDispatchLayouts["_tabRow"].visible = false;
        this.layoutEmptyTabs.panelBorder.visible = false;
        this.sourceServicefail.mode = DataSourceMode.ADD;
        this.addlDateValidations();
        this.setupStopEntry();
        this.formatAppointmentTimeDates();
        this.addBeforeLookupModelListeners();
        this.labelOrderId.addUnderlineWhenMouseOver();
        this.labelEmptyOrder.addUnderlineWhenMouseOver();
        this.labelStopsPickDrop.addUnderlineWhenMouseOver();
        this.textboxTrailerId.printableLabel.wrap = false;
        this.textboxDolly.printableLabel.wrap = false;
    }

    async performSave(): Promise<boolean> {
        try {
            const isEmpty = this.switchDispatchAction.checked;
            const stopSource = isEmpty ? this.layoutStopEntry.sourceStop : this.sourceStop;
            if (!await this.validateDate(isEmpty, stopSource)) {
                return false;
            }
            if (!this.activeRow.isNull("trailer2_id") || !this.activeRow.isNull("trailer3_id")) {
                if (!this.hasDoublesCertification())
                    return false;
            }
            this.dispatchButton.busy = true;
            if (isEmpty) {
                return await this.createEmpty();
            } else {
                if (this.panelTrailerWarnings.visible) {
                    CommonDialogs.showError(this.labelTrailerWarning.text + "\n Cannot complete dispatch.");
                    return false;
                }
                return !!(await this.sourceDispatchAsset.post((error: ErrorParam) => this.handlePostError(error)));
            }
        } catch {
            return false;
        } finally {
            this.dispatchButton.busy = false;
        }
    }

    private handlePostError(error: ErrorParam) {
        const message: string = error.toString();
        if (message && message.toLowerCase().includes("trailer is required")) {
            this.showTrailerMissingDialog();
            return;
        }
        CommonDialogs.showError(error);
    }

    private async showTrailerMissingDialog() {
        const prev = this.activeRow.get("prev_cleared_stop");
        const curr = this.activeRow.get("uncleared_stop");

        // Call the static method from the new class
        const dialog = new SelectTrailerDialog(prev, curr);
        const selectedTrailerId: string = await dialog.show();

        if (selectedTrailerId) {
            // Apply the selected trailer
            const newTrailerLmData: ModelRow = new ModelRow(
                this.textboxTrailer.lookupModel,
                false,
                {id: selectedTrailerId}
            );

            this.activeRow.setValueAndLookupModelData("trailer_id", selectedTrailerId, newTrailerLmData);
            this.checkForTrailerContinuity();
        }
    }

    /** This is an event handler for the beforeExecution event of sourceDispatchAsset. */
    sourceDispatchAssetBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.UPDATE) {
            this.activeRow.set("uncleared_stop", this.sourceStop.activeRow.data);
            this.activeRow.set("trailer_changed", this.activeRow.getChangedData()?.hasOwnProperty("trailer_id"));
            this.activeRow.set("trailer_dropped_changed", this.activeRow.getChangedData()?.hasOwnProperty("trailer_dropped"));
            this.activeRow.set("trailer2_dropped_changed", this.activeRow.getChangedData()?.hasOwnProperty("trailer2_dropped"));
            this.activeRow.set("trailer3_dropped_changed", this.activeRow.getChangedData()?.hasOwnProperty("trailer3_dropped"));
            this.activeRow.set("trailer2_changed", this.activeRow.getChangedData()?.hasOwnProperty("trailer2_id"));
            this.activeRow.set("trailer3_changed", this.activeRow.getChangedData()?.hasOwnProperty("trailer3_id"));
            this.activeRow.set("dolly_changed", this.activeRow.getChangedData()?.hasOwnProperty("dolly_id"));
            this.activeRow.set("dolly2_changed", this.activeRow.getChangedData()?.hasOwnProperty("dolly2_id"));
            if (this.panelServiceIncident.visible && this.sourceServicefail.hasChanged()) {
                if (this.sourceServicefail.validate()) {
                    this.sourceServicefail.activeRow.set("driver_id", this.activeRow.get("driver_id"));
                    this.sourceServicefail.activeRow.set("id", null);
                    this.activeRow.set("service_incident", this.sourceServicefail.activeRow.data);
                } else {
                    event.stop();
                }
            }
        }
    }

    /** This is an event handler for the afterExecution event of sourceDispatchAsset. */
    async sourceDispatchAssetAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.mainDataSource.mode = DataSourceMode.UPDATE;
            this._ordersCustomerId = this.mainDataSource.activeRow?.get("customer_id");
            const stops: ModelRow[] = this.activeRow.get("stops").map(stop => this.newStopRow(stop, false));
            const unclearedStopRow = this.newStopRow(this.activeRow.get("uncleared_stop"), false);
            this.setLocationContactTooltip(this.panelStopDetails, unclearedStopRow);
            this.sourceStop.setRowsAndMode(DataSourceMode.UPDATE, [unclearedStopRow]);
            this.layoutStopProgress.setStops(stops, ()=>{});
            this.displayApptStatus(this.panelApptStatus, this.imageApptConfirmed, this.labelApptStatus, unclearedStopRow);
            if (this.tabsetDispatchLayouts.getActiveTab() == this.tabCurrent) {
                const requireTrailer = getDispatchControlBoolean("require_pickup_trailer");
                this.validateEquipment();
                this.setupStopNotesAndRefNo();
                this.displayOtherStops();
                this.displayDriverCallins();
                this.setupDropTrailers();
                this.setupEquipAlerts();
                this.setupTrackingHistory();
                this.checkForServiceIncident();
                this.setupDriverPTA();                
                this.checkForTrailerContinuity();
                this.textboxTrailer.caption = requireTrailer == true ? "*Trailer" : "Trailer";
                this.showDrivers();
                this.setupActualArrival();
                this.setupTrailersAndDollies();
                this.determineOrderNumberVisibility();
                this.removeAuditLogContextMenuFromTextboxes();
                DetentionUtil.checkStopDetention(this.sourceStop.activeRow, "detention_status", this.imageDetention);
            }
            this.setStopTimezone();
            this.addTooltips();
        } else if (event.getAction() === DataSourceAction.UPDATE) {
            this.hasChanged = true;
            this.slideOut();
        }
        this.filterStandardCodes();
    }

    // Because of limitations getting audit log context menu items from models without a table, we have to manually remove them here for now
    private removeAuditLogContextMenuFromTextboxes() {
        const textboxes = this.getRecursiveChildren().filter(comp =>
            (comp instanceof Textbox || comp instanceof Checkbox) && comp.dataSource === this.sourceDispatchAsset
        );
        textboxes.forEach(textbox => {
            if (Array.isArray(textbox.contextMenuItems)) {
                textbox.contextMenuItems
                    = textbox.contextMenuItems.filter((item: { text: string }) => !item.text.includes(Dispatch.AUDIT_LOG_TEXT));
            }
        });
    }

    private setupActualArrival() {
        if (this.sourceStop.activeRow?.get("status") === "A" )
            this.textboxActualArrival.required = false;
    }

    getCurrentDispatchId(): string {
        return this.getEquipmentMove("dispatching_movement").get(("id"));
    }

    getEquipmentMove(type: string): ModelRow {
        const equipmentMoveData = this.sourceDispatchMovements.activeRow?.get(type, null);
        return equipmentMoveData ? new ModelRow(this.sourceMovement.url, false, equipmentMoveData) : null;
    }

    async validateEquipment() {
        this.validationResult = await EquipmentGroupValidation.validate({
            modelRow: this.activeRow,
            checkInProgress: true,
            tractorId: this.activeRow.get("tractor_id", null),
            driverId: this.activeRow.get("driver_id", null),
            driver2Id: this.activeRow.get("driver2_id", null),
            trailerId: this.activeRow.get("trailer_id", null),
        });

        this.layoutEquipmentGroupWarnings.display(this.validationResult);
        if (this.validationResult.driverEvents?.length > 0){
            EquipmentGroupValidation.showPriorityEventSnackbar(this.validationResult.driverEvents, EquipmentGroupValidation.driverEventDispatchFilter);
        }
        if (EquipmentGroupValidation.hasDispatchErrors(this.validationResult)) {
            this.dispatchButton.disabledTooltip = this.getEquipmentDisabledTooltip(false);
        }
        this.updateDispatchButtonState();
    }

    addlDateValidations() {
        this.textboxActualArrival.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.dateValidator(true, this.textboxActualArrival, this.textboxActualDeparture, mode);
        };
        this.textboxActualDeparture.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.dateValidator(false, this.textboxActualArrival, this.textboxActualDeparture, mode);
        };
        this.layoutStopEntry.textboxActualDeparture.addBlurListener((event: BlurEvent) => this.validateDateOnBlur(event, true));
    }

    private dateValidator(isArrive: boolean, arriveComp: Textbox, departComp: Textbox, mode: DataSourceMode): ValidationResult {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        if (!StringUtil.isEmptyString(arriveComp.text) && !StringUtil.isEmptyString(departComp.text)) {
            const arrivalDate = DateUtil.parseDateWithKeywords(arriveComp.text, true, true);
            const departureDate = DateUtil.parseDateWithKeywords(departComp.text, true, true);
            const dayDiff = DateUtil.dateDiff(DatePart.SECOND, departureDate, arrivalDate);

            if (dayDiff < 0) {
                return {
                    component: isArrive ? arriveComp : departComp,
                    isValid: false,
                    validationMessage: `The arrival date cannot be later than the departure date.`
                };
            } else {
                arriveComp.resetValidation();
                departComp.resetValidation();
            }
        }
        return null;
    }

    async validateDateOnBlur(event: BlurEvent, isEmpty: boolean = false): Promise<boolean> {
        if (event.changedWhileFocused) {
            const stopSource = (event.target as Textbox).dataSource;
            if (isEmpty && !stopSource.activeRow.isNull("actual_arrival")) {
                return;
            }
            await this.validateDate(isEmpty, stopSource);
        }
    }

    async validateDate(isEmpty: boolean = false, stopSource: DataSource) {
        try {
            const res = await Api.post("lme/dispatch/validate-stop", {
                stop_id: isEmpty ? null : stopSource.activeRow.get("id"),
                stop_data: stopSource.activeRow.data,
                earliest_allowed_date: this.activeRow.get("earliest_allowed_date", null)
            });
            if (res != null) {
                this.displayServiceFailure(res.data[0].has_service_failure != null);
                if (res.data[0].validated_stop_data) {
                    if (!isEmpty) {
                        stopSource.activeRow.setValues(res.data[0].validated_stop_data);
                    } else {
                        stopSource.setRowsAndMode(DataSourceMode.ADD,
                            [this.newStopRow(res.data[0].validated_stop_data, isEmpty)]);
                    }
                    this.setStopTimezone();
                }
            }
            return true;
        } catch (e) {
            this.panelServiceIncident.visible = false;
            Snackbar.showWarningSnackbar(e.messages[0]);
            return false;
        }
    }

    hasDoublesCertification(): boolean {
        const uncertifiedDrivers = [];
        if (!this.activeRow.getBoolean("driver_doubles_certified")) {
            uncertifiedDrivers.push(this.textboxDriverId.getLookupDisplayValues());
        }
        if (!this.activeRow.getBoolean("driver2_doubles_certified") && !this.activeRow.isNull("driver2_id")) {
            uncertifiedDrivers.push(this.textboxDriver2Id.getLookupDisplayValues());
        }
        if (uncertifiedDrivers.length > 0) {
            const driverText = uncertifiedDrivers.length > 1 ? uncertifiedDrivers.join(", ") : uncertifiedDrivers[0];
            CommonDialogs.showError(`Driver${uncertifiedDrivers.length > 1 ? "s" : ""} ${driverText} ${uncertifiedDrivers.length > 1 ? "are" : "is"} not certified to haul multiple trailers`);
            return false;
        }
        return true;
    }

    newStopRow(data, appending: boolean): ModelRow {
        return new ModelRow(this.sourceStop.url, appending, data);
    }

    setupStopNotesAndRefNo(): void {
        OrderStop.setTableParentDataSource(this.layoutStopNotesAndRefNumber.tableStopComments, this.sourceDispatchAsset);
        OrderStop.setTableParentDataSource(this.layoutStopNotesAndRefNumber.tableStopReferenceNumbers, this.sourceDispatchAsset);
        this.layoutStopNotesAndRefNumber.stopRow = this.sourceStop.activeRow;
        this.layoutStopNotesAndRefNumber.saveChangesFromEdit(this.layoutStopNotesAndRefNumber); // Used to refresh each table
    }

    displayOtherStops(): void {
        this.panelNextStop.visible = !this.activeRow.isNull("next_stop");
        this.nextStop = new ModelRow(this.sourceStop.url, false, this.activeRow.get("next_stop", {}));
        this.displayApptStatus(this.panelNextApptStatus, this.imageNextApptConfirmed, this.labelNextApptStatus, this.nextStop);
        this.imageNextStop.name = this.activeRow.get("uncleared_stop").stop_type == "SO" ? ImageName.pickup : ImageName.delivery;
        this.panelNextStopInfo.displayData(this.nextStop, null, 0);
        if (!this.nextStop.isNull("sched_arrive_early")) {
            this.textboxNextSchedArriveEarly.text = PlanningUtil.formatDateWindow(this.nextStop.getDateTime("sched_arrive_early"), this.nextStop.getDateTime("sched_arrive_late", null));
        }
        if (!this.nextStop.isNull("location_id")) {
            this.setLocationContactTooltip(this.panelNextStopDetails, this.nextStop);
        }
    }

    private displayDriverCallins() {
        const driverId = this.activeRow.get("driver_id");
        const tractorId = this.activeRow.get("tractor_id");
        if (driverId == null) return;
        this.layoutDriverCallins.initialize({
            driverId: driverId, tractorId: tractorId, nextStop: this.nextStop
        }, this.textboxCurrentLocation);
    }

    displayApptStatus(apptStatusPanel: Panel, apptImage: Image, apptLabel: Label, stop: ModelRow) {
        const required = stop.get("appt_required", "N") == "Y";
        const confirmed = stop.get("confirmed", "N") == "Y";
        if (required || confirmed) {
            apptImage.color = confirmed ? "success" : "caution";
            apptLabel.text = confirmed ? "Confirmed" : "Required";
        }
        apptStatusPanel.visible = required || confirmed;
    }

    setStopTimezone() {
        const abbr = this.activeRow.get("uncleared_stop").stop_timezone_id;
        const zone = Timezone.getFromAbbreviation(abbr);
        this.textboxActualArrival.timezone =
        this.textboxActualDeparture.timezone =
        this.textboxSchedArriveEarly.timezone =
        this.textboxSchedArriveLate.timezone = zone;

        this.addTimezoneToTextboxes(this.textboxSchedArriveEarly, abbr);
        this.addTimezoneToTextboxes(this.textboxSchedArriveLate, abbr);

        if (!this.activeRow.isNull("next_stop") && this.activeRow.get("next_stop").stop_timezone_id) {
            const nextStopZone = Timezone.getFromAbbreviation(this.activeRow.get("next_stop").stop_timezone_id);
            this.addTimezoneToTextboxes(this.textboxNextSchedArriveEarly, nextStopZone.daylightAbbreviation);
        }
    }

    addTimezoneToTextboxes(textbox: Textbox, timezone: string) {
        const end = textbox.text.trimEnd().slice(-3);
        // if the last 3 characters are a number, it got an update or has not had timezone added
        if (!isNaN(Number(end))) {
            textbox.text = textbox.text + " " + timezone;
        }
    }

    addTooltips() {
        CommonTooltips.setTooltipContentLayout(this.textboxDriverId, this.activeRow.get("driver_id"), "lme/dispatch/asset/DriverQuickInfo");
        CommonTooltips.setTooltipContentLayout(this.textboxDriver2Id, this.activeRow.get("driver2_id"), "lme/dispatch/asset/DriverQuickInfo");
        CommonTooltips.setTooltipContentLayout(this.textboxTractorId, this.activeRow.get("tractor_id"), "lme/dispatch/asset/TractorQuickInfo");
        CommonTooltips.setTooltipContentLayout(this.textboxTrailerId, this.activeRow.get("trailer_id"), "lme/dispatch/asset/TrailerQuickInfo");
        CommonTooltips.setTooltipContentLayout(this.labelTrailer2Id, this.activeRow.get("trailer2_id"), "lme/dispatch/asset/TrailerQuickInfo");
        CommonTooltips.setTooltipContentLayout(this.labelTrailer3Id, this.activeRow.get("trailer3_id"), "lme/dispatch/asset/TrailerQuickInfo");
        CommonTooltips.setTooltipContentLayout(this.textboxDolly, this.activeRow.get("dolly_id"), "lme/dispatch/asset/DollyQuickInfo");
        CommonTooltips.setTooltipContentLayout(this.labelDolly2Id, this.activeRow.get("dolly2_id"), "lme/dispatch/asset/DollyQuickInfo");

    }

    setLocationContactTooltip(locationComp: Component, stop: ModelRow) {
        locationComp.tooltipDynamicContent = async () => {
            const layout = await Layout.getLoadedLayout("lme/dispatch/LocationContactQuickInfo") as LocationContactQuickInfo;
            await layout.mainDataSource.search({ id: stop.get("location_id") });
            const refNumbers = stop.get("reference_numbers");
            layout.textboxReferenceNumbers.text = refNumbers.map(refNum => refNum.reference_number).join(", ");
            return layout;
        };
    }

    setupStopEntry() {
        this.layoutStopEntry.mainDataSource.mode = DataSourceMode.ADD;
        this.layoutStopEntry.getTimezone = this.layoutStopEntry.isDelivery = true;
        this.layoutStopEntry.textboxStopType.visible =
        this.layoutStopEntry.panelStopCommentsReferenceNumbers.visible =
        this.layoutStopEntry.panelAdditionalDetails.visible =
        this.layoutStopEntry.textboxStopType.required = false;
        this.layoutStopEntry.stopType = "SO";
        this.layoutStopEntry.textboxPickupDate.caption = "Delivery";
        this.layoutStopEntry.textboxSchedArriveEarly.caption = "Delivery Window";
        this.layoutStopEntry.panelDropTrailer.add(this.panelDropTrailer);
    }

    async setupEquipAlerts(){
        const alertsLayout = this.layoutDispatchEquipAlerts;
        await alertsLayout.mainDataSource.search({units:[this.activeRow.get("tractor_id"),
            this.activeRow.get("driver_id"), this.activeRow.get("trailer_id"),this.activeRow.get("trailer2_id"), this.activeRow.get("trailer3_id") ]});
        const rows = alertsLayout.mainDataSource.data;
        const urgentCount = rows.reduce((acc, cur) => cur.get("priority") == "U" ? ++acc : acc, 0);
        const tab = this.tabEquipmentAlerts;
        if(rows.length > 0){
            tab.imageName = ImageName.equipmentAlertMessages;
        }
        if(urgentCount > 0 ){
            tab.imageName = ImageName.equipmentAlertUrgent;
        }
        tab.visible = true;

    }

    checkForTrailerContinuity() {
        if (!this.activeRow.isNull("trailer_id") || !this.activeRow.isNull("trailer2_id") || !this.activeRow.isNull("trailer3_id")) {
            const status = this.getEquipmentMove("dispatching_movement").get("status");
            const tcw = this.layoutTrailerContinuityWarnings;
            tcw.mainDataSource.search({
                tractor_id: this.activeRow.get("tractor_id"),
                trailer_id: this.activeRow.get("trailer_id", null),
                trailer2_id: this.activeRow.get("trailer2_id", null),
                trailer3_id: this.activeRow.get("trailer3_id", null),
                stop_id: status == "P" ? this.activeRow.get("prev_cleared_stop").id : this.activeRow.get("uncleared_stop").id
            }).then(() => {
                const hasWarnings = tcw.mainDataSource.rowCount > 0;
                this.panelTrailerWarnings.visible = hasWarnings;
                if (hasWarnings) {
                    this.labelTrailerWarning.text = tcw.mainDataSource.data.reduce((res, data) => res += (data.get("message") + "\n"), "");
                    this.buttonLocate.visible = this.buttonLocate.enabled = this.labelTrailerWarning.text.includes("is not available");
                    this.panelLocate.visible = false;
                    this.stopForLocate = null;
                    this.setupButtonLocate();
                    this.addLocateStops();
                }
            });
        } else {
            this.panelTrailerWarnings.visible = false;
        }
    }


    sameStop(stop1, stop2) {
        if (stop1.location_id && stop2.location_id) {
            return stop1.location_id == stop2.location_id;
        } else {
            return stop1.city_name == stop2.city_name && stop1.state == stop2.state && stop1.zip_code == stop2.zip_code;
        }
    }

    addLocateStops() {
        this.panelLocateStops.removeAll();
        const status = this.getEquipmentMove("dispatching_movement").get("status");
        if (status != "P") {
            this.panelLocateStops.add(this.buildLocateStop(this.activeRow.get("uncleared_stop")));
        }
        const prevClearedStop = this.activeRow.get("prev_cleared_stop");
        if (prevClearedStop != null && !this.sameStop(prevClearedStop, this.activeRow.get("uncleared_stop"))) {
            this.panelLocateStops.add(this.buildLocateStop(prevClearedStop));
        }
    }

    buildLocateStop(stopData): Panel {
        const panelLocateStop = CloneComponent.clone({ component: this.panelLocateLocation, appendComponentId: false, id: `panelLocateStop-${stopData.id}` }) as Panel;
        panelLocateStop.visible = true;
        panelLocateStop.addClickListener((event: ClickEvent) => this.locateStopOnClick(panelLocateStop, stopData));
        panelLocateStop.displayData(new ModelRow(this.sourceStop.url, true, stopData), null, 0);
        return panelLocateStop;
    }

    locateStopOnClick(selectedPanel: Panel, stopData) {
        this.panelLocateStops.components.forEach((comp: Panel) => {
            const selected = comp == selectedPanel && comp.backgroundColor != "primary";
            comp.applyTempState({ backgroundColor: selected ? "primary" : "default.reverse", color: selected ? "default.reverse" : "default" });
        });
        this.stopForLocate = this.stopForLocate == stopData ? null : stopData;
        this.buttonLocate.enabled = this.stopForLocate != null;
    }

    async buttonLocateOnClick(event: ClickEvent) {
        if (this.panelLocate.visible) {
            const results: ValidationResult[] = this.textboxLocateActualArrival.validate(true, true);
            if (!results[0]?.isValid) {
                Snackbar.showWarningSnackbar("Unable to save this record because data is missing or invalid from the Locating Trailer's Actual Arrival.");
                return;
            }
            const res = await this.locateTrailer();
            if (!res) return;
        }
        this.panelLocate.visible = !this.panelLocate.visible;
        this.setupButtonLocate();
        this.buttonLocate.enabled = this.stopForLocate != null;
    }

    setupButtonLocate() {
        this.buttonLocate.color = this.panelLocate.visible ? "primary" : "error";
        this.buttonLocate.caption = this.panelLocate.visible ? "Move Trailer" : "Locate Trailer";
    }

    async locateTrailer(): Promise<boolean> {
        if (this.layoutTrailerContinuityWarnings.selectedTrailer == null) {
            CommonDialogs.showError("A trailer must be selected to move.");
            return false;
        }
        const data = {
            action: Action.MOVE,
            trailer_id: this.layoutTrailerContinuityWarnings.selectedTrailer,
            date: this.textboxLocateActualArrival.getDataValue(),
            ...this.stopForLocate
        }
        try {
            await Api.post("lme/dispatch/asset/seating", data);
            Toast.showSuccessToast("Trailer Located Successfully");
            this.checkForTrailerContinuity();
            return true;
        } catch (e) {
            CommonDialogs.showError(e)
            return false;
        }
    }

    addBeforeLookupModelListeners() {
        this.textboxTrailer.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'A');
        this.textboxDollyId.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'A');
        this.textboxDolly2Id.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'A');
        this.textboxTrailer2Id.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'A');
        this.textboxTrailer3Id.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'A');
    }

    async setupTrackingHistory() {
        this.layoutTrackingHistory.visible = this.tabTracking.visible;

        if (this.tabTracking.visible) {
            const tractorId = this.activeRow?.get("tractor_id", null);
            this.layoutMovementMap.padding = 0;
            this.layoutMovementMap.mapStatus.height = undefined;
            this.layoutTrackingHistory.tablePositions.maxHeight = null;
            this.layoutTrackingHistory.tablePositions.fillHeight = true;
            this.layoutTrackingHistory.tableCallins.maxHeight = null;
            this.layoutTrackingHistory.tableCallins.fillHeight = true;

            const moveId = this.getCurrentMovementId();

            this.layoutTrackingHistory.mainDataSource.search({
                movement_id: moveId,
                callin_id: tractorId,
                days_back: 5
            })
            this.layoutMovementMap.mainDataSource.search({
                movement_id: moveId,
                include_positions: !this.layoutTrackingHistory.switchPositionCallin.checked,
                days_back: 5
            })

            this.layoutTrackingHistory.switchPositionCallin.addChangeListener((event: ChangeEvent) => {
                this.layoutMovementMap.search({
                    movement_id: moveId,
                    include_positions: !this.layoutTrackingHistory.switchPositionCallin.checked,
                    days_back: 5
                })
            });
        }
    }

    getCurrentMovementId(): string {
        let moveId: string = null;
        const dispatchingMovement = this.getEquipmentMove("dispatching_movement");
        
        if (dispatchingMovement && dispatchingMovement.get("status") === "A") {
            // Try to use previous movement instead
            const prevMovement = this.getEquipmentMove("prev_movement");
            if (prevMovement && prevMovement.get("status") !== "A") {
                moveId = prevMovement.get("id");
            }
        } else if (dispatchingMovement) {
            moveId = dispatchingMovement.get("id");
        }
        
        if (!moveId) {
            moveId = this.activeRow?.get("movement_id", null);
        }
        return moveId;
    }

    showDrivers() {
        const driver2Exists = !this.activeRow.isNull("driver2_id");
        this.panelDriver2Info.visible = driver2Exists;
        this.textboxDriverId.caption = driver2Exists ? "Driver One" : "Driver";
        this.textboxDriverPTA.caption = driver2Exists ? "Driver One PTA" : "Driver PTA";
    }

    /* SERVICE INCIDENT LOGIC - START */
    /** This is an event handler for the afterModeChange event of sourceServicefail. */
    sourceServicefailAfterModeChange(event: DataSourceModeChangeEvent) {
        this.sourceServicefail.activeRow.set({
            fault_of_carrier_or_driver: true,
            edi_code_version: "004010",
            edi_element_id: "1651",
            caused_by_type: "D"
        });
    }

    checkForServiceIncident() {
        new ModelServicefail().searchSingle({ stop_id: this.sourceStop.activeRow?.get("id") }).then((row: ModelRow) => {
            if (row != null) {
                this.sourceServicefail.setRowsAndMode(DataSourceMode.NONE, [row]);
            }
        });
    }

    setupDriverPTA(){
        this.panelDriverPTA.visible = this.activeRow?.getBoolean("show_driver_pta", false);
        this.panelDriver2PTA.visible = !this.activeRow?.isNull("driver2_id") && this.activeRow?.getBoolean("show_driver2_pta", false);
        this.labelLastHome.visible = !this.activeRow?.isNull("driver_last_home");
        this.labelNextHome.visible = !this.activeRow?.isNull("driver_next_home");
        this.panelHomes.visible = this.labelLastHome.visible || this.labelNextHome.visible;
        this.labelLastHome2.visible = !this.activeRow?.isNull("driver2_last_home");
        this.labelNextHome2.visible = !this.activeRow?.isNull("driver2_next_home");
        this.panelHomes2.visible = this.labelLastHome2.visible || this.labelNextHome2.visible;
    }

    displayServiceFailure(hasServiceFailure: boolean) {
        if (this.sourceServicefail.mode == DataSourceMode.ADD) {
            this.panelServiceIncident.visible = hasServiceFailure;
        }
    }
    /* SERVICE INCIDENT LOGIC - END */

    /* DROP TRAILER LOGIC - START */
    setupDropTrailers() {
        this.checkForPrevTrailer();
        this.switchDropTrailer.visible = this.dropTrailerVisible();

        // Set initialization flag to prevent change handlers from modifying data
        this.isInitializingDropTrailer = true;

        // Store the current field values and original data before UI initialization
        const currentTrailer1Dropped = this.activeRow.getBoolean("trailer_dropped", false);
        const currentTrailer2Dropped = this.activeRow.getBoolean("trailer2_dropped", false);
        const currentTrailer3Dropped = this.activeRow.getBoolean("trailer3_dropped", false);

        this.switchDropTrailer.addChangeListener((event: ChangeEvent) => this.switchDropTrailerOnChange(event));

        if (this.maxTrailers > 1 && this.switchDropTrailer.visible === true) {
            this.switchDropTrailer.caption = "Dropping Trailer(s)";
            // Check if any trailers have been dropped and set the switch state
            const anyTrailerDropped = currentTrailer1Dropped || currentTrailer2Dropped || currentTrailer3Dropped;

            this.switchDropTrailer.checked = anyTrailerDropped;

            // If any trailer is dropped, show checkboxes and set their states during initialization
            if (anyTrailerDropped) {
                this.checkboxTrailerDropped.visible = true;
                this.checkboxTrailer2Dropped.visible = true;
                this.checkboxTrailer3Dropped.visible = this.maxTrailers > 2;

                // Update enabled state based on trailer IDs
                this.updateTrailerCheckboxEnabledStates();

                // Set checkbox states - they will update the fields but we'll restore original data
                this.checkboxTrailerDropped.checked = currentTrailer1Dropped;
                this.checkboxTrailer2Dropped.checked = currentTrailer2Dropped;
                if (this.maxTrailers > 2) {
                    this.checkboxTrailer3Dropped.checked = currentTrailer3Dropped;
                }
            } else {
                // Hide checkboxes if no trailers are dropped
                this.checkboxTrailerDropped.visible = false;
                this.checkboxTrailer2Dropped.visible = false;
                this.checkboxTrailer3Dropped.visible = false;
            }
        } else if (this.maxTrailers === 1 && this.switchDropTrailer.visible === true) {
            this.switchDropTrailer.checked = currentTrailer1Dropped;
        }

        // Clear initialization flag
        this.isInitializingDropTrailer = false;

    }

    switchDropTrailerOnChange(event: ChangeEvent) {
        // Don't modify data during initialization
        if (this.isInitializingDropTrailer) {
            return;
        }

        if ( (event.target as Switch).checked ) {
            if (this.maxTrailers > 1) {
                this.checkboxTrailerDropped.visible = true;
                this.checkboxTrailer2Dropped.visible = true;
                this.checkboxTrailer3Dropped.visible = this.maxTrailers > 2;

                // Update enabled state based on trailer IDs
                this.updateTrailerCheckboxEnabledStates();

                const trailerFields = ["trailer_dropped", "trailer2_dropped", "trailer3_dropped"];
                const anyFieldsSet = trailerFields.slice(0, this.maxTrailers).some(field =>
                    this.activeRow.getBoolean(field, false)
                );

                if (!anyFieldsSet) {
                    // If no fields are set, check all checkboxes by default (but only if enabled)
                    if (this.checkboxTrailerDropped.enabled) {
                        this.checkboxTrailerDropped.checked = true;
                    }
                    if (this.checkboxTrailer2Dropped.enabled) {
                        this.checkboxTrailer2Dropped.checked = true;
                    }
                    if (this.maxTrailers > 2 && this.checkboxTrailer3Dropped.enabled) {
                        this.checkboxTrailer3Dropped.checked = true;
                    }
                }
            }
            else {
                this.activeRow.set("trailer_dropped", true);
            }
        }
        else {
            if (this.maxTrailers > 1) {
                this.checkboxTrailerDropped.visible = false;
                this.checkboxTrailer2Dropped.visible = false;
                this.checkboxTrailer3Dropped.visible = false;

                // Set all trailer dropped fields to false
                this.activeRow.set("trailer_dropped", false);
                this.activeRow.set("trailer2_dropped", false);
                this.activeRow.set("trailer3_dropped", false);
            }
            if (this.maxTrailers === 1) {
                this.activeRow.set("trailer_dropped", false);
            }
        }
    }

    dropTrailerVisible(): boolean {
        return !this.activeRow.isNull("trailer_id") && this.activeRow.getBoolean("is_last_stop", false);
    }

    private updateTrailerCheckboxEnabledStates() {
        // Enable/disable checkboxes based on whether the corresponding trailer ID exists
        if (this.checkboxTrailerDropped.visible) {
            const hasTrailer1 = !this.activeRow.isNull("trailer_id");
            if (!hasTrailer1 && this.checkboxTrailerDropped.checked) {
                this.checkboxTrailerDropped.checked = false;
            }
            this.checkboxTrailerDropped.enabled = hasTrailer1;
        }
        if (this.checkboxTrailer2Dropped.visible) {
            const hasTrailer2 = !this.activeRow.isNull("trailer2_id");
            if (!hasTrailer2 && this.checkboxTrailer2Dropped.checked) {
                this.checkboxTrailer2Dropped.checked = false;
            }
            this.checkboxTrailer2Dropped.enabled = hasTrailer2;
        }
        if (this.maxTrailers > 2 && this.checkboxTrailer3Dropped.visible) {
            const hasTrailer3 = !this.activeRow.isNull("trailer3_id");
            if (!hasTrailer3 && this.checkboxTrailer3Dropped.checked) {
                this.checkboxTrailer3Dropped.checked = false;
            }
            this.checkboxTrailer3Dropped.enabled = hasTrailer3;
        }
    }

    /* DROP TRAILER LOGIC - END */

    /* EMPTY LOGIC - START */
    createEmptyButton(): Image {
        return new Image({
            rowBreak: false,
            name: ImageName.add,
            width: 30,
            height: 30,
            marginRight: 16,
            marginLeft: 8,
            color: "success",
            onClick: () => {}
        })
    }

    async createEmpty(): Promise<boolean> {
        if (this.tieEmptyMovementId == null) {
            Snackbar.showWarningSnackbar("Please select an empty move position.");
            return false;
        }
        try {
            if (this.layoutStopEntry.sourceStop.validate()) {
                await Api.post("lme/dispatch/asset/empty-stop", {
                    equipment_id: this.equipmentId,
                    tractor_id: this.activeRow.get("tractor_id", null),
                    driver_id: this.activeRow.get("driver_id", null),
                    position: this.emptyPosition,
                    tie_empty_movement_id: this.tieEmptyMovementId,
                    trailer_pickup_id: this.activeRow.get("trailer_pickup_id"),
                    drop_trailer: this.activeRow.get("drop_trailer"),
                    stop_data: this.layoutStopEntry.sourceStop.activeRow
                });
                this.hasChanged = true;
                const newStopRow = await this.layoutStopEntry.sourceStop.createBlankRow();
                this.layoutStopEntry.sourceStop.setRowsAndMode(DataSourceMode.ADD, [newStopRow]);
                this.layoutStopEntry.citystateCityStateZip.enabled = true;
                this.addTabs();
                return true;
            }
        } catch {
            return false;
        }
        return false;
    }

    private loadEmpties(type: string) {
        const emptyMoves = this.sourceDispatchMovements.activeRow.get(`${type}_empties`, []);
        const emptyMoveRows = emptyMoves.map(move => new ModelRow(this.sourceMovement.url, false, move));
        if (type == "prev") {
            this.layoutPrevEmpty.isPrevious = true;
            this.layoutPrevEmpty.tableEmptyMoves.displayData(null, emptyMoveRows, 0);
        } else {
            this.layoutNextEmpty.isPrevious = false;
            this.layoutNextEmpty.tableEmptyMoves.displayData(null, emptyMoveRows, 0);
        }
    }
    /* EMPTY LOGIC - END */

    /* TAB LOGIC - START */
    /** This is an event handler for the onChange event of switchDispatchAction. */
    switchDispatchActionOnChange(event) {
        this.panelDispatchSection.visible = !event.newValue;
        this.switchDropTrailer.visible = !event.newValue && this.dropTrailerVisible();
        this.panelEmptySection.visible = event.newValue;
        this.addDispatchToChangedComp();
    }

    addDispatchToChangedComp() {
        const isEmpty = this.switchDispatchAction.checked;
        this.dispatchButton.removeFromHasChangedComponents();
        if (isEmpty) {
            this.layoutStopEntry.mainDataSource.addHasChangedComponent(this.dispatchButton);
        } else {
            this.sourceDispatchAsset.addHasChangedComponent(this.dispatchButton);
        }
        this.updateDispatchButtonState();
    }

    updateDispatchButtonState() {
        const isEmpty = this.switchDispatchAction.checked;
        const hasChanged = isEmpty ? this.layoutStopEntry.mainDataSource.hasChanged() : this.sourceDispatchAsset.hasChanged();
        this.dispatchButton.enabled = isEmpty ? !EquipmentGroupValidation.hasDriverRegulatoryErrors(this.validationResult) && hasChanged :
            !EquipmentGroupValidation.hasDispatchErrors(this.validationResult) && hasChanged;

        if (EquipmentGroupValidation.hasDispatchErrors(this.validationResult)) {
            this.dispatchButton.disabledTooltip = this.getEquipmentDisabledTooltip(isEmpty);
        }
    }

    getEquipmentDisabledTooltip(isEmpty: boolean) {
        return `${isEmpty ? "Creating an empty" : "Dispatch"} is disabled due to invalid equipment.`
    }

    private loadMovementTabs(movementTabs: MovementTabs, showEmpty: boolean = false) {
        movementTabs.currentTabId = null;
        movementTabs.selectedMovementId = this.getCurrentDispatchId();
        movementTabs.addTabProps({
            hideOrder: true,
            overrideActiveTab: true,
            tabStyleProps: {
                ...(showEmpty && {
                    borderBottomWidth: 1,
                    borderBottomColor: "subtle.light",
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                })
            },
            tabSpacer: (showEmpty ? this.createEmptyButton() : null)
        });
        movementTabs.movementTabOnClickCallback = (movement) => this.movementTabOnClick(movement, movementTabs);
        movementTabs.movementTabOnAdd = (movement) => this.movementTabOnAdd(movement, movementTabs);
        movementTabs.doBeforeTabClick = () => this.hasTabChanged();
        movementTabs.getTab = () => this.getDispatchingTab(movementTabs);
        movementTabs.addMovementTabs(this.getMovements());
    }

    private getMovements(): ModelRow[] {
        const movements:ModelRow[] = [];
        const prevMovement = this.getEquipmentMove("prev_movement");
        if (prevMovement)
            movements.push(prevMovement);

        const prevEmpty = this.getEmpty("prev");
        if (prevEmpty != null)
            movements.push(prevEmpty);

        const dispatchingMovement = this.getEquipmentMove("dispatching_movement");
        if (dispatchingMovement)
            movements.push(dispatchingMovement);

        const nextEmpty = this.getEmpty("next");
        if (nextEmpty != null)
            movements.push(nextEmpty);

        const nextMovement = this.getEquipmentMove("next_movement");
        if (nextMovement)
            movements.push(nextMovement);
        return movements;
    }

    private inheritEquipmentFromPreviousMovement(fieldName: string, droppedFieldName: string | null, lookupModel: string
    ): void {
        const prevMove = this.getEquipmentMove("prev_movement");
        const prevEmpty = this.getEmpty("prev");

        // The backend returns movements ordered by actual_arrival desc (most recent first).
        // If prev_empties has items, they were encountered before prev_movement in that ordering,
        // meaning prevEmpty is always more recent than prevMove when both exist.
        // Therefore: prefer prevEmpty over prevMove.
        const movement: ModelRow = prevEmpty || prevMove;

        if (movement != null &&
            this.activeRow.isNull(fieldName) &&
            !movement.isNull(fieldName) &&
            (droppedFieldName == null || !movement.getBoolean(droppedFieldName, false))) {

            this.activeRow?.setValueAndLookupModelData(fieldName, movement.get(fieldName),
                new ModelRow(lookupModel, false, {"id": movement.get(fieldName)}));
        }
    }

    private checkForPrevTrailer() {
        // Inherit trailers (with dropped checks)
        this.inheritEquipmentFromPreviousMovement("trailer_id", "trailer_dropped", this.textboxTrailer.lookupModel);
        this.inheritEquipmentFromPreviousMovement("trailer2_id", "trailer2_dropped", this.textboxTrailer2Id.lookupModel);
        this.inheritEquipmentFromPreviousMovement("trailer3_id", "trailer3_dropped", this.textboxTrailer3Id.lookupModel);

        // Inherit dollies (no dropped checks for dollies)
        this.inheritEquipmentFromPreviousMovement("dolly_id", null, this.textboxDollyId.lookupModel);
        this.inheritEquipmentFromPreviousMovement("dolly2_id", null, this.textboxDolly2Id.lookupModel);
    }

    private getEmpty(type: string) {
        const empties = this.sourceDispatchMovements.activeRow.get(`${type}_empties`, []);
        if (empties.length > 0) {
            const emptyRow = new ModelRow(this.sourceMovement.url, false, {
                ...empties[0], // we want to use the most recent empty's data
                empty_type: type,
            })
            if (empties.length > 1) {
                emptyRow.set("empty_count", empties.length);
            }
            return emptyRow;
        }
        return null;
    }

    private getDispatchingTab(movementTabs: MovementTabs) {
        return movementTabs.getMovementTab(this.getEquipmentMove("dispatching_movement"));
    }

    private movementTabOnAdd(movement: ModelRow, movementTabs: MovementTabs) {
        const tabSpacer = movementTabs.getTabSpacer(movement.get("id")) as Image;
        const movementTab = movementTabs.getMovementTab(movement);
        const isPrev = this.getEquipmentMove("prev_movement")?.get("id") == movement.get("id");
        const isPrevEmpty = this.getEmpty("prev")?.get("id") == movement.get("id");
        const isNext = this.getEquipmentMove("next_movement")?.get("id") == movement.get("id");
        const shouldDispatch = movement.get("movement_id", movement.get("id")) == this.getCurrentDispatchId();
        const isEmpty = movement.get("loaded") == "E";
        if (tabSpacer != null) {
            if (isPrev || isPrevEmpty) {
                const dispatchingStatus = this.getEquipmentMove("dispatching_movement").get("status");
                tabSpacer.visible = (isPrev && this.getEmpty("prev") == null || isPrevEmpty) && dispatchingStatus != "D";
                tabSpacer.enabled = dispatchingStatus != "P";
                tabSpacer.color = tabSpacer.enabled ? "success" : "subtle.light";
                tabSpacer.disabledTooltip = "Cannot insert empty because current move is not delivered";
            }
            movementTab.tab.enabled = false; // disable click for empty tabs
            tabSpacer.addClickListener((event: ClickEvent) => this.tabSpacerOnClick(movementTabs, movement, tabSpacer));
            if (isNext) {
                tabSpacer.visible = false;
            }
        }
        movementTab.tabInfo.setProps({ paddingTop: 0, height: 50 });
        movementTabs.manageDispatchToppers(shouldDispatch, isPrev, isNext, isEmpty, movementTab);
    }

    async movementTabOnClick(movement: ModelRow, movementTabs: MovementTabs) {
        this.panelMainContent.visible = false;
        const movementId = movement.get("movement_id", movement.get("id"));
        const isPrev = movementId == this.getEquipmentMove("prev_movement")?.get("id");
        const isDispatch = movementId == this.getCurrentDispatchId()
        const isNext = movementId == this.getEquipmentMove("next_movement")?.get("id");

        if (isPrev) {
            this.tabPrev.select();
            if (!this.layoutDispatchPrev.activeRow) {
                this.layoutDispatchPrev.search({ id: movementId });
            }
        } else if (isNext) {
            this.tabNext.select();
            if (!this.layoutDispatchNext.activeRow) {
                this.layoutDispatchNext.search({ id: movementId });
            }
        } else if (isDispatch) {
            this.tabCurrent.select();
        }

        if (movement.get("loaded") == "E" && !isDispatch && !isPrev) {
            this.panelOverview.visible = this.layoutStopProgress.visible = false;
            const emptyType = movement.get("empty_type", null);
            this.loadEmpties(emptyType);
            if (emptyType == "prev") {
                this.tabPrevEmpty.select();
            } else {
                this.tabNextEmpty.select();
            }
        } else {
            this.panelOverview.visible = this.layoutStopProgress.visible = true;
        }

        this.dispatchButton.visible = isDispatch;
        this.switchDispatchAction.checked = false;
        this.panelMainContent.visible = true;
    }

    private async hasTabChanged() {
        const stopSource = this.switchDispatchAction.checked ? this.layoutStopEntry.sourceStop : this.sourceStop;
        const emptyRequiredFields = ["city_name", "zip_code", "state", "location_id", "sched_arrive_early"];
        const hasEmptyStopChanged = () => emptyRequiredFields.some(field => stopSource.activeRow.getChangedData().hasOwnProperty(field));
        if (this.switchDispatchAction.checked ? hasEmptyStopChanged() : stopSource.hasChanged()) {
            const saveChanges = await CommonDialogs.showYesNo("Save your changes on this tab before switching?", "Save Changes");
            if (saveChanges == undefined) {
                return false;
            } else if (saveChanges) {
                return await this.performSave();
            } else {
                this.displayData(null, null, 0);
                this.layoutStopEntry.displayData(null, null, 0);
                this.panelServiceIncident.visible = false;
            }
            return true;
        }
    }

    addTabs() {
        this.layoutMovementTabs.panelMovementTabInfo.removeAll();
        this.layoutEmptyTabs.panelMovementTabInfo.removeAll();
        this.sourceDispatchMovements.search({ equipment_id: this.equipmentId }).then(async () => {
            await this.sourceDispatchAsset.search({ equipment_id: this.equipmentId, order_id: this.orderId });
            this.loadMovementTabs(this.layoutMovementTabs);
            this.loadMovementTabs(this.layoutEmptyTabs, true);
            this.switchDispatchAction.checked = false;
        })
        this.tabsetDispatch.components.forEach((tab:Tab) => {
            tab.heading.tooltip = new Label({
                text: tab.displayLabel as string
            })
            tab.heading.tooltipPosition = Alignment.RIGHT;
        })
    }

    private tabSpacerOnClick(movementTabs: MovementTabs, movement: ModelRow, tabSpacer: Image) {
        movementTabs.panelMovementTabInfo.components.forEach((component) => {
            if (component instanceof Image) {
                if (component.id == tabSpacer.id) {
                    const movementId = movement.get("id");
                    const prevMove = this.getEquipmentMove("prev_movement");
                    const isPrev = this.getEmpty("prev") ? movementId == this.getEmpty("prev")?.get("id")
                        : movementId == prevMove?.get("id");
                    const dispatchIsDelivered = movementId == this.getCurrentDispatchId() // In this case we want to create empty from previous
                        && this.getEquipmentMove("dispatching_movement").get("status") == "D";
                    this.emptyPosition = (isPrev || dispatchIsDelivered) ?  "PREV" : "AFTER";
                    this.tieEmptyMovementId = movementId;
                    this.layoutStopEntry.panelActualTimes.visible =
                    this.layoutStopEntry.panelDropTrailer.visible = isPrev || dispatchIsDelivered;
                    component.name = ImageName.circleCheck;
                    component.height = 24;
                    if (!isPrev && !dispatchIsDelivered) {
                        this.layoutStopEntry.sourceStop.activeRow.set({
                            actual_arrival: null, actual_departure: null
                        });
                    }
                } else {
                    component.name = ImageName.add;
                    component.height = 30;
                }
            }
        })
    }
    /* TAB LOGIC - END */

    /** This is an event handler for the afterTabSelection event of tabsetDispatch. */
    tabsetDispatchAfterTabSelection(event:SelectionEvent) {
        this.dispatchButton.visible = event.newValue == 0;
        if (event.newValue == 2) {
            if (this.layoutMovementMap.mapStatus.vendor === MapVendor.TRIMBLE) {
                this.layoutMovementMap.mapStatus.map.fitPins();
            }

            // Refresh call-ins & positions when switching to tracking tab
            const tractorId = this.activeRow?.get("tractor_id", null);
            const moveId = this.getCurrentMovementId();
            this.layoutTrackingHistory.mainDataSource.search({
                movement_id: moveId,
                callin_id: tractorId,
                days_back: 5
            });
        }
    }

    filterStandardCodes(){
        if(!StringUtil.isEmptyString(this.ordersCustomerId)){
            this.textboxEdiStandardCode.addBeforeLookupModelSearchListener(event => {
                event.filter.customer_id = this.ordersCustomerId;
                event.filter.code_type = "DelayReason";
                event.filter.element_id = "1651";
                event.filter.use_enabled_flag = "Y";
            });
        }
        else{
            this.textboxEdiStandardCode.lookupModel = "lme/datafusion/edi-standard-code";
            this.textboxEdiStandardCode.lookupModelLayout = "lme/datafusion/EdiStandardCodeLookup";
            this.textboxEdiStandardCode.addBeforeLookupModelSearchListener(event => {
                event.filter.element_id = "1651";
                event.filter.version = "004010";
            });
        }
    }

    formatAppointmentTimeDates() {
        const dateTimeFormat = "EEEE, MMM d " + DateUtil.getUserTimeFormat();
        this.textboxSchedArriveEarly.format = dateTimeFormat;
        this.textboxSchedArriveLate.format = dateTimeFormat;
    }

    public get ordersCustomerId(): String {
        return this._ordersCustomerId;
    }

    /** This is an event handler for the onLookupModelSelection event of textboxTrailer 1/2/3. */
    textboxTrailerOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.checkForTrailerContinuity();
        this.validateEquipment();
        this.updateTrailerCheckboxEnabledStates();
    }

    private orderNumberOnClick(event: ClickEvent) {
        PlanningUtil.navigateToOrder(event);
    }

    labelEmptyOrderOnClick(event: ClickEvent) {
        OrderTabView.navigateTo({ key: this.activeRow.get("movement_id"), orderId: this.activeRow.get("order_id", "none"), newTab: true });
    }

    stopPickDropOnClick(event: ClickEvent) {
        MultiStop.showSlideout({ isManifest: false, row: this.activeRow,
            doAfterSlideOut: (success: boolean) => {
                if (success) {
                    this.addTabs();
                }
            }
        });
    }

    setupTrailersAndDollies() {
        if (this.maxTrailers && this.maxTrailers > 1) {
            this.panelDollyInfo.visible = true;
            this.textboxStopWeight.rowBreak = false; // don't want driver PTA on 3rd line if weight wraps
            this.textboxTrailerId.caption = "Trailers";
            if (this.maxTrailers >= 2) {
                this.panelMaxTrailers2.visible = true;
                this.labelTrailer2Id.visible = true;
			}
            if (this.maxTrailers == 3) {
                this.textboxDolly.caption = "Dollies";
                this.panelMaxTrailers3.visible = true;
                this.labelTrailer3Id.visible = true;
                this.labelDolly2Id.visible = true;
            }
        }
    }

    determineOrderNumberVisibility() {
        this.panelEmptyOrder.visible = this.getEquipmentMove("dispatching_movement").get("loaded") == "E";
        this.panelOrderNumber.visible = !this.panelEmptyOrder.visible;
    }

    private showMultiStopSlideout(event: ClickEvent): void {
        const image = event.target as Image;
        image.busy = true;
        try {
            this.createEditRowDecorator(this.activeRow?.get(Dispatch.ORDER_FIELD), image);
        } finally {
            image.busy = false;
        }
    }

    private async createEditRowDecorator(orderId: string, image: Image): Promise<void> {
        const multiStopLayout = Layout.getLayout("lme/dispatch/MultiStop") as MultiStop;
        const erd = new EditRowDecorator({
            title: `Stop Details - Order ${orderId}`,
            layout: multiStopLayout,
            layoutLoadListeners: async () => {
                await multiStopLayout.mainDataSource.search({id: orderId});
                multiStopLayout.mainDataSource.mode = DataSourceMode.UPDATE;
                multiStopLayout.activeRow.set(Dispatch.ORDER_FIELD, orderId);
                setCommodityTooltipContent(multiStopLayout.textboxCommodityId, multiStopLayout.activeRow);
            },
            width: '80%',
            overlayProps: {closeOnClickOff: false, greyedBackground: true},
            onSave: async () => {
                multiStopLayout.postChanges();
            },
            onClose: () => multiStopLayout.slideOut(),
            doAfterSlideIn: () => image.busy = false,
            onValidate: () => {
                const validationResults:ValidationResult[] = multiStopLayout.validate(true, true);
                if (!ValidationUtil.checkValidationResults(validationResults)) {
                    ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                }
                return validationResults;
            }
        });
    }
}
